// The fetch-plan decision shared by the keep-alive data hooks (keep-alive-page-
// host AC3/AC4). When a kept page is re-activated, its hook re-fetches *silently*
// — bypassing the browser cache (to catch a change made elsewhere) and holding
// the last-known content on screen (no drop to a loading skeleton, no blank-
// then-populate). A first load, a page/filter change, or a post-write reload
// behaves as before. This pure function owns that decision so it can be tested
// without React.

export interface FetchTrigger {
  // True iff this fetch was triggered by a post-write reload() (read-cache-
  // strategy) — bypasses cache, and shows the loading state as it always has.
  isReload: boolean;
  // True iff this fetch was triggered by an on-return activation revalidation —
  // bypasses cache (catch elsewhere-changes) but is silent.
  isRevalidate: boolean;
  // True iff the hook currently holds ready content to keep on screen. A silent
  // revalidation only suppresses the loading state when there is content to
  // hold; with nothing on screen yet there is no flash to avoid, so it falls
  // back to the normal loading path.
  hasContent: boolean;
}

export interface FetchPlan {
  // Whether to drop to the loading state before fetching. False for a silent
  // on-return revalidation that has content to hold.
  showLoading: boolean;
  // Whether to bypass the browser cache for this fetch.
  bypassCache: boolean;
}

export function fetchPlan(t: FetchTrigger): FetchPlan {
  const silent = t.isRevalidate && t.hasContent;
  return {
    showLoading: !silent,
    // A reload or a revalidation both bypass cache so the user sees fresh data;
    // a plain page/filter change re-reads cacheable.
    bypassCache: t.isReload || t.isRevalidate,
  };
}
